import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { getScaledPaperLimits } from '@domainUtils'
import { PlanTemplateImage, PlanTemplateImageProps, selectPreviewedPlanSettings } from '@scene'
import { AxiosError } from 'axios'
import type { FileDTO } from '@api/fastAPI.schemas'
import { ensurePlanTemplateImage } from '@api/plan-template-images/plan-template-images'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useControlStore } from '@editorStores'
import { useGetPlanSettings, useSceneElement } from '@editorHooks'
import { PLAN_VIEW_TEMPLATE_TYPES, PlanViewPlanType } from '../constants'

const DISABLED_PROPERTIES: PlanTemplateImageProps = { url: '', xlim: [0, 0], ylim: [0, 0] }

/**
 * "Vorlage anzeigen": draws the plan template of the current plan configuration (plan type, paper
 * format, orientation from the PDF plan settings) under the floorplan, exactly over the plan area
 * frame, so the preview shows the paper the export lays the plan on.
 *
 * The backend renders the template to an image once per configuration and keeps it as a project
 * file (the request is an idempotent "ensure"), so the image is only generated the first time a
 * configuration is previewed and reused on every later visit.
 *
 * While a plan settings form previews unsaved values in the scene, the template follows them
 * (`selectPreviewedPlanSettings`), like the plan area frame does. Only the saved paper format and
 * orientation are requested, though: a previewed one shows its template if it is cached already,
 * otherwise none until the settings are saved, so flipping through formats in the form doesn't
 * make the backend render and store a template for each.
 *
 * Returns whether the image is currently being requested (generated or fetched).
 */
const usePlanTemplateImage = (
  enabled: boolean,
  planType: PlanViewPlanType,
): { isLoadingTemplateImage: boolean } => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { data: savedPlanSettings } = useGetPlanSettings()
  const previewedPlanSettings = useControlStore(selectPreviewedPlanSettings)
  const planSettings = previewedPlanSettings ?? savedPlanSettings
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const templateType = PLAN_VIEW_TEMPLATE_TYPES[planType]
  const paperFormat = planSettings?.paperFormat
  const orientation = planSettings?.orientation
  const isSavedConfiguration =
    paperFormat === savedPlanSettings?.paperFormat && orientation === savedPlanSettings?.orientation

  const { data: templateImage, isFetching } = useQuery<FileDTO, AxiosError>({
    queryKey: ['planTemplateImage', projectId, templateType, paperFormat, orientation],
    queryFn: () =>
      ensurePlanTemplateImage(
        projectId as string,
        templateType,
        paperFormat as NonNullable<typeof paperFormat>,
        orientation as NonNullable<typeof orientation>,
      ),
    // Disabled for a previewed configuration: the query then only serves an already cached image.
    enabled: enabled && isSavedConfiguration && !!projectId && !!paperFormat && !!orientation,
    // The image only changes when the organisation replaces its template; the plan configuration
    // is part of the key, so there is nothing to refetch for.
    staleTime: Infinity,
    onError: error => {
      enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Planvorlage'), {
        variant: 'error',
      })
    },
  })

  const url = templateImage?.url
  const properties = useMemo<PlanTemplateImageProps>(() => {
    if (!planSettings || !url) return DISABLED_PROPERTIES
    const { xlim, ylim } = getScaledPaperLimits(planSettings)
    return { url, xlim, ylim }
  }, [planSettings, url])

  useSceneElement({
    // Like the 2D floorplan image, the paper is only meaningful in the top-down plan view.
    enabled: enabled && isOrthographic && properties !== DISABLED_PROPERTIES,
    properties,
    component: PlanTemplateImage,
  })

  return { isLoadingTemplateImage: isFetching }
}

export default usePlanTemplateImage
